// Executing a behaviour artifact over the scanned window, inside the isolated
// Python realm, with the emission contract as the only exit.
//
// The artifact is free — real Python, full authority over the windows it is
// handed — and everything it returns passes the same boundary the declarative
// tier passes: host-stamped identity and call state, schema-validated values
// and claims, and a refusal for any occurrence keyed to a block id the window
// never contained. The three failure classes stay separate the whole way:
// THREW (the artifact crashed or the realm refused), INVALID (rows the
// contract rejected), EMPTY (an honest absence).

import { runPythonInRealm, releasePythonExecutionRealm } from "./pythonRealmRuntime.js";
import {
  RUN_SURFACES,
  createUnknownRunContext,
  resolvedKernelFromModel,
} from "./kernelResolver.js";
import { windowsFromBodies } from "./xrayFactoryCorpus.js";
import { manifestOf } from "./behaviourArtifactStore.js";
import { validateEmissionBatch, entitiesFromValues } from "../../xray-factory/src/emit/contract.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

const RUN_TIMEOUT_MS = 30_000;
const REALM_IDLE_MS = 180_000;

// Artifact code is untrusted, so each run id gets its own PUBLISHED realm
// worker — durable by design, and nothing else ever releases it. Left alone,
// every distinct artifact (and every edit, since the version is in the id)
// parks a live worker until page reload. The lease bounds that in three ways:
// an idle worker evicts after a period of disuse, a superseded version's
// worker retires as soon as it is not executing, and the number of behaviours
// holding a worker at once is capped LRU.
//
// LAUNCH order decides which version is current, never completion order: runs
// of different versions overlap (the trace feed runs artifacts per trace-open,
// edits re-run them), and destroying a worker fails every run still executing
// on it — so eviction of any run id waits until that id has nothing in flight.
const MAX_LEASED_WORKERS = 8;
const realmLeases = new Map();        // runId -> idle timer
const inflightRuns = new Map();       // runId -> live run count
const staleRunIds = new Set();        // retired; released once not in flight
const currentByBehaviour = new Map(); // behaviour -> current runId (launch order)
const lastLaunchByBehaviour = new Map(); // behaviour -> monotonic seq, for LRU
let launchSeq = 0;

onAuthIdentityChange(() => {
  const runIds = new Set([
    ...realmLeases.keys(),
    ...inflightRuns.keys(),
    ...currentByBehaviour.values(),
  ]);
  currentByBehaviour.clear();
  lastLaunchByBehaviour.clear();
  for (const runId of runIds) retireWhenIdle(runId);
});

function retireWhenIdle(runId) {
  clearTimeout(realmLeases.get(runId));
  realmLeases.delete(runId);
  if (inflightRuns.get(runId) > 0) {
    staleRunIds.add(runId);
    return;
  }
  staleRunIds.delete(runId);
  releasePythonExecutionRealm(runId);
}

function retireBehaviour(behaviour) {
  const runId = currentByBehaviour.get(behaviour);
  currentByBehaviour.delete(behaviour);
  lastLaunchByBehaviour.delete(behaviour);
  if (runId) retireWhenIdle(runId);
}

function beginRealmRun(behaviour, runId) {
  inflightRuns.set(runId, (inflightRuns.get(runId) || 0) + 1);
  staleRunIds.delete(runId);
  clearTimeout(realmLeases.get(runId));
  realmLeases.delete(runId);
  lastLaunchByBehaviour.set(behaviour, ++launchSeq);
  const current = currentByBehaviour.get(behaviour);
  if (current !== runId) {
    if (current) retireWhenIdle(current);
    currentByBehaviour.set(behaviour, runId);
  }
  if (currentByBehaviour.size > MAX_LEASED_WORKERS) {
    let coldest = null;
    for (const [b, seq] of lastLaunchByBehaviour) {
      if (b !== behaviour && (!coldest || seq < coldest.seq)) coldest = { b, seq };
    }
    if (coldest) retireBehaviour(coldest.b);
  }
}

function endRealmRun(behaviour, runId) {
  const left = (inflightRuns.get(runId) || 1) - 1;
  if (left > 0) inflightRuns.set(runId, left);
  else inflightRuns.delete(runId);

  if (staleRunIds.has(runId)) {
    if (left <= 0) {
      staleRunIds.delete(runId);
      releasePythonExecutionRealm(runId);
    }
    return;
  }
  // Only the behaviour's current version idles toward eviction, and only once
  // nothing of it is executing.
  if (currentByBehaviour.get(behaviour) !== runId || left > 0) return;
  const timer = setTimeout(() => {
    realmLeases.delete(runId);
    if (currentByBehaviour.get(behaviour) === runId) {
      currentByBehaviour.delete(behaviour);
      lastLaunchByBehaviour.delete(behaviour);
    }
    releasePythonExecutionRealm(runId);
  }, REALM_IDLE_MS);
  timer.unref?.();
  realmLeases.set(runId, timer);
}

/** Test-only: drop every lease without waiting out the idle window. */
export function _releaseArtifactRealmsForTests() {
  for (const [runId, timer] of realmLeases) {
    clearTimeout(timer);
    releasePythonExecutionRealm(runId);
  }
  realmLeases.clear();
  inflightRuns.clear();
  staleRunIds.clear();
  currentByBehaviour.clear();
  lastLaunchByBehaviour.clear();
}

// The driver appended to every artifact. `emit(window)` is the single entry
// point; a missing definition or a raised exception surfaces as the realm
// run's error (THREW), never as an empty reading.
const DRIVER = `

_occurrences = []
for _window in windows:
    _emitted = emit(_window)
    if _emitted:
        _occurrences.extend(_emitted)
_occurrences`;

/**
 * Run one artifact over the factory's scanned bodies.
 *
 * @returns {{
 *   ok: boolean,
 *   threw: string|null,
 *   occurrences: object[],           // contract-valid, host-stamped
 *   invalid: {index, problems}[],    // rows the boundary refused
 *   manifestProblems: string[],
 *   windows: number,
 *   emitted: number,
 * }}
 */
export async function runArtifactOverBodies(artifact, bodies, { signal, timeoutMs = RUN_TIMEOUT_MS } = {}) {
  const manifest = manifestOf(artifact);
  const windows = windowsFromBodies(bodies);
  const base = {
    occurrences: [], invalid: [], manifestProblems: [], windows: windows.length, emitted: 0,
  };
  if (!windows.length) return { ...base, ok: true, threw: null };

  const code = `${artifact.source}${DRIVER}`;
  const runContext = createUnknownRunContext({ surface: RUN_SURFACES.CHAT });
  const resolvedDefinition = resolvedKernelFromModel({ kind: "execute_python", code }, runContext);

  // Version-keyed, so an edited artifact can never inherit module state its
  // previous build left in the worker; the lease retires the old version.
  const runId = `artifact:${getAuthIdentityEpoch()}:${artifact.behaviour}@${manifest.version}`;
  beginRealmRun(artifact.behaviour, runId);
  let ret;
  try {
    ret = await runPythonInRealm({
      resolvedDefinition,
      runContext,
      code,
      globals: { windows },
      attribution: { kind: "behaviour-artifact", behaviour: artifact.behaviour },
      runId,
      timeoutMs,
      signal,
    });
  } catch (err) {
    return { ...base, ok: false, threw: err?.message || String(err) };
  } finally {
    endRealmRun(artifact.behaviour, runId);
  }
  if (!ret?.ok) {
    return {
      ...base,
      ok: false,
      threw: [ret?.error || "the realm returned no result", ret?.stderr].filter(Boolean).join("\n"),
    };
  }

  const raw = Array.isArray(ret.result) ? ret.result : [];

  // Host stamping. Identity and transport state are the HOST's knowledge: an
  // artifact that could set its own version could impersonate another build,
  // and one that could set `call` could claim a result the trace never
  // recorded. Keys are joined back to the windows for the same reason — an
  // occurrence keyed to a block id that was never scanned breaks the residual
  // accounting and the graph joins, so it is refused by name.
  const callByKey = new Map();
  for (const w of windows) for (const c of w.calls) callByKey.set(c.key, { call: c.call, w });
  const stamped = [];
  // Contract refusals below index into `stamped`, which is `raw` minus the
  // key-refused rows — reported as-is, a batch mixing both refusal kinds names
  // the wrong emitted row (and can name one row twice). This maps a stamped
  // position back to the raw position the author can actually look up.
  const rawIndexOf = [];
  const invalid = [];
  raw.forEach((r, index) => {
    const o = r && typeof r === "object" ? r : {};
    const known = callByKey.get(String(o.key || ""));
    if (!known) {
      invalid.push({ index, problems: [`key "${o.key}" is not a block id in the scanned windows — occurrences must key on real tool_use blocks`] });
      return;
    }
    const occ = {
      behaviour: manifest.behaviour,
      version: manifest.version,
      key: String(o.key),
      call: known.call,
      ...(known.w.sessionId ? { sessionId: known.w.sessionId } : {}),
      ...(known.w.endpointId ? { endpointId: known.w.endpointId } : {}),
      ...(known.w.ts ? { ts: known.w.ts } : {}),
      values: o.values && typeof o.values === "object" ? o.values : {},
      claims: o.claims && typeof o.claims === "object" ? o.claims : {},
      ...(Array.isArray(o.entities) ? { entities: o.entities } : {}),
    };
    const entities = entitiesFromValues(manifest, occ);
    stamped.push(entities.length && !occ.entities ? { ...occ, entities } : occ);
    rawIndexOf.push(index);
  });

  const batch = validateEmissionBatch(manifest, stamped);
  return {
    ok: true,
    threw: null,
    occurrences: batch.valid,
    // Key refusals and schema refusals are one list to the reader — both are
    // rows the artifact emitted and the contract sent back.
    invalid: [...invalid, ...batch.invalid.map((r) => ({ ...r, index: rawIndexOf[r.index] }))],
    manifestProblems: batch.manifestProblems,
    windows: windows.length,
    emitted: raw.length,
  };
}
